import { resolveAppleArtworkOnly, appleArtworkOnlyNow } from '../../shared/appleArtwork';

// True on the browser web app AND on Capacitor native (iOS/Android). Both
// share the same characteristics — HTTP-based API shim (no Electron IPC),
// mobile-first responsive layout, no DJ-migration features, samples-only
// playback. False on real Electron desktop where preload.js exposes an IPC
// `db` object backed by SQLite.
//
// How native passes this check: capacitor-shim deliberately doesn't set a
// `db` property on window.electron (only `__native: true`), so the
// `!window.electron.db` check is true on native exactly like on web. This
// is the SAME literal expression used by 20+ files with local isWeb defs
// — keeping the shape identical means those local defs work unchanged.
export const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;

// Capacitor-only (iOS/Android). Used to differentiate native from browser
// for the few places where the distinction matters (NativeMusicPlayer vs
// MusicPlayer, MobilePaywall vs WebPlaybackCTA, IAP flow, etc.).
export const isNative = typeof window !== 'undefined'
    && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

// Apple-only artwork (src/shared/appleArtwork.js), the same switch the shims use: the live
// server mode once known (utils/appleArtworkMode.js), else the build-time value — on for
// iOS/Android, off for web/desktop, REACT_APP_APPLE_ARTWORK_ONLY=1/0 overrides.
const APPLE_ARTWORK_BUILD = resolveAppleArtworkOnly(process.env.REACT_APP_APPLE_ARTWORK_ONLY, isNative);
export const appleArtworkOnly = () => appleArtworkOnlyNow(isNative, APPLE_ARTWORK_BUILD);

// True wherever full-stream playback is NOT available: web (app.mixgoat.com),
// iOS, Android. Equivalent to isWeb now that isWeb includes native. Kept as
// a named export so playability checks self-document.
export const isSamplesOnly = isWeb;

// Phone-layout environment: Capacitor native, or a phone-sized web viewport
// in either orientation (mirrors the 768px CSS breakpoint the mobile layout
// uses — see FeedTab's isMobileWebEnv). A function, not a constant, so
// non-component code (thunks) evaluates the CURRENT viewport. Always false
// on Electron desktop.
export const isMobileEnv = () => isWeb
    && typeof window.matchMedia === 'function'
    && (isNative
        || window.matchMedia('(max-width: 768px)').matches
        || window.matchMedia('(max-height: 480px)').matches);

// STRICTLY iOS (Capacitor getPlatform === 'ios'). False on web, Electron
// desktop, AND Android. Used to suppress — ONLY on iOS — the "available on the
// desktop app" upsell messaging and the mix play affordances (App Store
// Review). Do NOT widen this to isWeb/isNative — that would leak the iOS-only
// behavior to web/desktop/Android.
export const isIOS = typeof window !== 'undefined'
    && !!(window.Capacitor && typeof window.Capacitor.getPlatform === 'function'
        && window.Capacitor.getPlatform() === 'ios');

// Repeat-action toast text (red error). Track playback is omitted because
// sample/preview playback works fine on web — only full playback is
// desktop-only, and the first-play modal already explains that. Showing an
// error toast on every repeat play implies all playback failed, which is
// wrong; it should stay silent and let the minimized CTA do its job.
export const WEB_ONLY_TOAST_MESSAGES = {
    mix: 'Mixes playback is only available on the desktop app',
    'set-planner': 'Set planner is only available on the desktop app',
    'crate-digger': 'Crate digger is only available on the desktop app',
};

// source: 'action' (user clicked play/etc.) or 'hint' (passive tab-mount nudge).
// Repeat 'action' triggers show a red toast; repeat 'hint' triggers are silent.
export function notifyWebOnly(type = 'track', source = 'action') {
    if (!isWeb) return;
    window.dispatchEvent(new CustomEvent('web-playback-cta', { detail: { type, source } }));
}
